
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Button from '../../../components/base/Button';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  features: string[];
  slug: string;
  is_active: boolean;
  sort_order: number;
}

export default function ServicesSection() {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    if (!supabase) {
      // Fallback data if Supabase is not available
      setServices([
        {
          id: '1',
          title: 'Ticaret ve Şirketler Hukuku',
          description: 'Şirket kuruluşu, ortaklık anlaşmaları, ticari sözleşmeler, birleşme ve devir işlemleri.',
          icon: 'ri-building-line',
          features: ['Şirket Kuruluşu', 'Ortaklık Anlaşmaları', 'Birleşme ve Devir'],
          slug: 'ticaret-sirketler-hukuku',
          is_active: true,
          sort_order: 1
        },
        {
          id: '2',
          title: 'Gayrimenkul ve İnşaat Hukuku',
          description: 'Tapu işlemleri, kira sözleşmeleri, emlak alım-satımı, inşaat sözleşmeleri ve uyuşmazlıkları.',
          icon: 'ri-home-line',
          features: ['Tapu İşlemleri', 'İnşaat Sözleşmeleri', 'Emlak Uyuşmazlıkları'],
          slug: 'gayrimenkul-insaat-hukuku',
          is_active: true,
          sort_order: 2
        },
        {
          id: '3',
          title: 'İş Hukuku ve Sosyal Güvenlik',
          description: 'İş sözleşmeleri, işçi hakları, işten çıkarma, SGK işlemleri ve iş kazası tazminatları.',
          icon: 'ri-briefcase-line',
          features: ['İş Sözleşmeleri', 'SGK İşlemleri', 'İşçilik Alacağı Davaları', 'Hizmet Tespit Davaları'],
          slug: 'is-hukuku-sosyal-guvenlik',
          is_active: true,
          sort_order: 3
        },
        {
          id: '4',
          title: 'Ceza Hukuku',
          description: 'Ceza Hukuku kapsamında tüm dava türleri ile ilgilenilmektedir.',
          icon: 'ri-scales-3-line',
          features: ['Suç Davaları', 'Savunma Stratejileri', 'Hukuki Temsil'],
          slug: 'ceza-hukuku',
          is_active: true,
          sort_order: 4
        },
        {
          id: '5',
          title: 'İcra ve İflas Hukuku',
          description: 'İcra takipleri, haciz işlemleri, iflas davaları ve alacak tahsilatı süreçleri.',
          icon: 'ri-file-list-3-line',
          features: ['İcra Takipleri', 'Haciz İşlemleri', 'Alacak Tahsilatı'],
          slug: 'icra-iflas-hukuku',
          is_active: true,
          sort_order: 5
        },
        {
          id: '6',
          title: 'Sözleşmeler Hukuku',
          description: 'Sözleşme hazırlama, müzakere, ihlal davaları ve sözleşmeli uyuşmazlık çözümü.',
          icon: 'ri-file-text-line',
          features: ['Sözleşme Hazırlama', 'Müzakere Süreçleri', 'İhlal Davaları'],
          slug: 'sozlesmeler-hukuku',
          is_active: true,
          sort_order: 6
        },
        {
          id: '7',
          title: 'Aile Hukuku',
          description: 'Boşanma, velayet, nafaka, mal paylaşımı, evlat edinme ve aile içi hukuki sorunlar.',
          icon: 'ri-heart-line',
          features: ['Boşanma Davaları', 'Velayet İşleri', 'Mal Rejimi Davaları', 'Ziynet Alacağı Davaları'],
          slug: 'aile-hukuku',
          is_active: true,
          sort_order: 7
        },
        {
          id: '8',
          title: 'İdare ve Vergi Hukuku',
          description: 'İdari davalar, vergi uyuşmazlıkları, kamu ihaleleri ve idari yaptırım davaları.',
          icon: 'ri-government-line',
          features: ['İdari Davalar', 'Vergi Uyuşmazlıkları', 'Kamu İhaleleri'],
          slug: 'idare-vergi-hukuku',
          is_active: true,
          sort_order: 8
        },
        {
          id: '9',
          title: 'Tazminat Hukuku',
          description: 'Maddi ve manevi tazminat davaları, trafik ve iş kazası tazminatları.',
          icon: 'ri-money-dollar-circle-line',
          features: ['Maddi Tazminat', 'Manevi Tazminat', 'Trafik Kazasından Kaynaklı Tüm Tazminat Talepleri', 'İş Kazasından Kaynaklı Tüm Tazminat Talepleri'],
          slug: 'tazminat-hukuku',
          is_active: true,
          sort_order: 9
        },
        {
          id: '10',
          title: 'Kişisel Verilerin Korunması Hukuku',
          description: 'Kişisel Verilerin Korunması Hukuku alanında danışmanlık hizmeti vermekteyiz.',
          icon: 'ri-shield-user-line',
          features: ['KVKK Uyum', 'Veri Güvenliği', 'Hukuki Danışmanlık'],
          slug: 'kisisel-verilerin-korunmasi',
          is_active: true,
          sort_order: 10
        },
        {
          id: '11',
          title: 'Miras Hukuku',
          description: 'Miras paylaşımı, veraset işlemleri, miras davaları ve miras hukuku danışmanlığı.',
          icon: 'ri-ancient-gate-line',
          features: ['Miras Paylaşımı', 'Mirasın Reddi', 'Veraset ve İntikal İşlemleri', 'İzale-i Şüyu Davaları'],
          slug: 'miras-hukuku',
          is_active: true,
          sort_order: 11
        }
      ]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (!error && data) {
        setServices(data);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (slug: string) => {
    navigate(`/services/${slug}`);
  };

  if (loading) {
    return (
      <section id="services" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
            <p className="text-gray-600 font-poppins">Hizmetler yükleniyor...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 font-poppins">
            Hizmetlerimiz
          </h2>
          <div className="w-16 h-0.5 bg-slate-900 mx-auto mb-6"></div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto font-poppins font-light">
            Geniş hukuk alanlarında uzman kadromuzla profesyonel hizmet sunuyoruz
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={service.id} 
              className="bg-white p-6 rounded-lg shadow-lg hover-lift animate-fade-in-up cursor-pointer group"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => handleServiceClick(service.slug)}
            >
              <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-800 transition-colors">
                <i className={`${service.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 font-poppins">
                {service.title}
              </h3>
              <p className="text-slate-600 mb-4 leading-relaxed font-poppins font-light">
                {service.description}
              </p>
              <ul className="space-y-2 mb-4">
                {service.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-sm text-slate-600 font-poppins font-light">
                    <i className="ri-check-line text-green-500 mr-2"></i>
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="flex items-center text-slate-900 font-semibold group-hover:text-slate-700 transition-colors group-hover:translate-x-1 transform duration-200 font-poppins">
                Ücretli Danışmanlık
                <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
